import { Source, Vector as VectorSource } from 'ol/source';
import { Layer, Vector as VectorLayer } from 'ol/layer';
import { Feature } from 'ol';
import { fromLonLat } from 'ol/proj';
import { Geometry, Point } from 'ol/geom';
import { Style, Icon } from 'ol/style';
import { Map as MGMap } from '@maersk-global/map-layers';

import { VoyagePlansModel } from '../models/voyage-plans-model';
import { VesselPlanningModel, VoyagePlan } from '../models/vessel-planning-model';
import { VoyageRoute, VoyageRoutingModel } from '../models/voyage-routing-model';
import MGPlayerCore, { PLAYER_EVENTS } from '@maersk-global/player-core';
import { PlanningLayers } from './planning-layers';
import { Coordinate } from 'ol/coordinate';
import { SvgIcons } from '../../local_modules/voyage-svg-icons/src/voyage-svg-icons';
import { SuggestedRouteEvents } from '../../local_modules/voyage-suggested-routes/src/types';

const _PROJECTION = (<any>window)["appenv"].projection;
const DEFAULT_SHIP_COLOR = "#003D6D";

const voyagePlanningStyle = new Style({
    image: new Icon({
        src: 'data:image/svg+xml,' + encodeURIComponent(SvgIcons.customVoyagePlanningVesselIcon.strings[0]),
        scale: 1,
        anchor: [0.5, 0.5],
        rotateWithView: true,
    })
})
export class VesselInTimeLayers extends EventTarget
{
    constructor(playerCore: MGPlayerCore, includePlanning?: boolean, isVessel?: boolean, planningLayers?: PlanningLayers)
    {
        super();
        this._includePlanning = !!includePlanning;
        this._isVessel = !!isVessel;
        this._planningLayers = planningLayers || null;
        this._player = playerCore;

        if (this._isVessel) {
            this._shipShape = escape(SvgIcons.customVoyagePlanningVesselIcon.strings[0])
        } else {
            this._shipShape = escape(SvgIcons.customVesselIcon.strings[0])
        }

        this._setPlanCurrent = this._setPlanCurrent.bind(this);
        this._setExecutingPlan = this._setExecutingPlan.bind(this);
        this._setPlansForRoutes = this._setPlansForRoutes.bind(this);
        this._plotVesselsInTime = this._plotVesselsInTime.bind(this);
    }

    get layers()
    {
        return this._layers;
    }

    async initialize()
    {
        this._vectorSources.push(new VectorSource({}));
        this._layers.push(new VectorLayer({source: this._vectorSources[0], visible: true, zIndex: 5}));    

        if (this._includePlanning)
        {
            for (let i = 0; i < VoyageRoutingModel!.uiRoutes.length; i++)
            {
                const source = new VectorSource({});
                this._vectorSources.push(source);
                this._layers.push(new VectorLayer({source: source, visible: true, zIndex: 5}));

                this._vesselScreenLocations.push([-1, -1]);
            }    
        }

        if (!this._isVessel)
        {
            VoyagePlansModel!.addEventListener('plans-updated', <EventListener>this._setPlanCurrent);
        }
        else
        {
            VesselPlanningModel!.addEventListener('executing-plan-changed', this._setExecutingPlan);
        }

        if (this._includePlanning)
        {
            VoyageRoutingModel!.addEventListener(SuggestedRouteEvents.RoutesChanged, this._setPlansForRoutes);
        }

        // Vessel 'in-the-future' position
        this._player.addEventListener(PLAYER_EVENTS.TIME_UPDATE, this._plotVesselsInTime);
    }

    assignMap(map: MGMap)
    {
        this._map = map;
    }

    clear()
    {
        try
        {
            for (let i = 0; i < this._routes.length; i++)
            {
                this._setRoute(i, null);
            }
        }
        catch (error)
        {
            console.warn(error);
        }
    }

    private _includePlanning: boolean;
    private _isVessel: boolean;
    private _planningLayers: PlanningLayers|null = null;
    private _layers: Layer<Source>[] = [];
    private _map: MGMap|null = null;
    private _player: MGPlayerCore;
    private _vectorSources: VectorSource<Geometry>[] = [];
    private _routes: (VoyageRoute|null)[] = [ null ];
    private _vesselScreenLocations: Coordinate[] = [ [-1, -1] ];
    private _selectedPlan: number = -1;
    private _shipShape: string;

    private _setPlanCurrent(evt: CustomEvent)
    {
        const plan = VoyagePlansModel!.choosePlan(evt.detail.imoNo);
        if (!plan || plan.routes.length == 0)
        {
            this._setRoute(0, null);
        }
        else
        {
            this._setRoute(0, plan.routes[plan.selectedRouteIndex]);
        }
    }

    private async _setExecutingPlan()
    {
        const plan = await VesselPlanningModel!.getExecutingPlan();

        if (!plan || plan.routes.length === 0 || plan.selectedRouteIndex < 0)
        {
            this._setRoute(0, null);
            return;
        }
        this._setRoute(0, plan.routes[plan.selectedRouteIndex]);
    }

    private _setPlansForRoutes()
    {
        this._selectedPlan = -1;
        const uiRoutes = VoyageRoutingModel!.uiRoutes;
        for (let i = 0; i < uiRoutes.length; i++)
        {
            this._setRoute(i+1, uiRoutes[i].route);
            if (uiRoutes[i].selected)
            {
                this._selectedPlan = i+1;
            }
        }
    }

    private _setRoute(index: number, plan: VoyageRoute|null)
    {
        this._routes[index] = plan;

        // Clear cache and source for this plan
        if (this._vectorSources[index])
        {
            this._vectorSources[index].clear();
        }

        // Redraw
        this._plotVesselsInTime();
    }

    private _plotVesselsInTime()
    {
        const playerTime = this._player.currentTime;

        if (playerTime <= Date.now())
        {
            // Clear vessel-in-time layer when at current time (or older)
            for (const vectorSource of this._vectorSources)
            {
                vectorSource.clear();
            }
            this.dispatchEvent(new CustomEvent('vessel-in-time-position', { detail: null }));
            return;
        }

        for (let i = 0; i < this._routes.length; i++)
        {
            const plan = this._routes[i];
            if (!plan || (this._isVessel && !(<VoyageRoute>plan).route_info))
            {
                continue; // no plan or plan not calculated
            }

            // timestamp of first and last waypoint of route
            const routeStartTime = new Date(plan.route_geometry[0].properties.timestamp).getTime();
            const routeEndTime = new Date(plan.route_geometry[plan.route_geometry.length-1].properties.timestamp).getTime();

            if ((<VoyageRoute>plan).route_info && (playerTime < routeStartTime || playerTime > routeEndTime)) {
                continue; // currentTime is outside of route start and end time
            }

            const positionAndHeading = VoyageRoutingModel!.getPositionAndHeadingInTime(playerTime, plan);
            if (!positionAndHeading) continue;

            const screenLocation = this._map!.getPixelFromCoordinate(fromLonLat(positionAndHeading.position, _PROJECTION));

            if (Math.abs(screenLocation[0] - this._vesselScreenLocations[i][0]) < 1
                && Math.abs(screenLocation[1] - this._vesselScreenLocations[i][1]) < 1)
            {
                continue;
            }

            if (i === this._selectedPlan || i === 0)
            {
                this.dispatchEvent(new CustomEvent('vessel-in-time-position', { detail: positionAndHeading.position }));
            }

            this._vesselScreenLocations[i] = screenLocation;

            let shipColor = DEFAULT_SHIP_COLOR;
            if (this._planningLayers && i > 0 && i-1 < this._planningLayers.colors.uiRouteColors.length)
            {
                shipColor = this._planningLayers.colors.uiRouteColors[i-1];
            }

            const rotate = this._isVessel ? 2 : 180;
            
            let shipStyle: Style;
            shipStyle = voyagePlanningStyle;

            shipStyle.getImage().setRotation(positionAndHeading.heading - Math.PI / rotate);

            // Use the projected line to get the exact map position (using fromLonLat() gives a slight error for long lines)
            const shipPoint = new Point(positionAndHeading.projectedPosition);
            const shipFeature = new Feature(shipPoint);
            shipFeature.setStyle(shipStyle);

            this._vectorSources[i].clear();
            this._vectorSources[i].addFeature(shipFeature);
        }
    }
};
